import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, Tooltip } from '@mui/material';
import {
  MdDragIndicator,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdPeople,
  MdSubdirectoryArrowRight,
  MdUnarchive,
} from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/context-menu/context-menu';
import {
  DeleteConfirmationModal,
  deleteTrackText,
} from '../../../../components/modals/delete-confirmation-modal';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { cn } from '../../../../components/utils';
import { getSubTrackContextMenuItems } from '../sub-track-context-menu';
import { TrackForEdit, TrackPreview } from '../types';
import {
  getEffectiveTrackColor,
  isTrackDeleteDisabled,
  stylesForIndex,
} from '../utils';
import { MoveTrackModal } from './MoveTrackModal';

import { trpc } from '../../../../../trpc/react';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { useToast } from '../../../../hooks/useToast';
import { useTrackMutations } from '../../hooks/useTrackMutations';

export function TrackSidePanelItem({
  track,
  allTracks,
  index,
  isLast,
  setTrackForEdit,
  openAddTrackDrawer,
  parentTrack,
  disabled,
  active,
  dropHighlight = null,
  isCollapsed,
  toggleCollapse,
  hasSubtracks,
  refetchTracks,
  isArchived = false,
  isFirstInSection = false,
  openViewArchivedTrackDrawer,
  openMoveSubTrack,
}: {
  track: TrackPreview;
  allTracks: TrackPreview[];
  index: number;
  isLast: boolean;
  setTrackForEdit: (forEdit: TrackForEdit) => void;
  openAddTrackDrawer: (open: boolean, parentTrack?: TrackPreview) => void;
  parentTrack?: TrackPreview;
  hasSubtracks?: boolean;
  disabled: boolean;
  active: boolean;
  // Set while this row's parent row + sub-track list is the group a
  // cross-parent sub-track drag is currently hovering over — 'eligible' or
  // 'ineligible' depending on whether the move would be refused for a
  // same-name collision. The drop itself is allowed either way; 'ineligible'
  // is an early visual cue ahead of the toast the refusal still shows.
  dropHighlight?: 'eligible' | 'ineligible' | null;
  isCollapsed: boolean;
  toggleCollapse: (trackId: string) => void;
  refetchTracks: () => void;
  isArchived?: boolean;
  isFirstInSection?: boolean;
  // Wired up for archived rows. Sub-track rows only become openable behind the
  // sub-track reorganisation flag; callers must also pass it down for them.
  openViewArchivedTrackDrawer?: (trackId: string) => void;
  // Opens the move flow for a sub-track. Called without a destination from the
  // context menu, which has not chosen one yet; the drag entry passes the
  // destination it dropped onto. Optional like openViewArchivedTrackDrawer,
  // because the archived rows are rendered by a caller that has no move flow.
  openMoveSubTrack?: (
    subTrack: TrackPreview,
    destinationTrack?: TrackPreview,
  ) => void;
}) {
  const {
    team: { teams },
    org: {
      settings: { enableTeamLibrary },
    },
    profile: { role },
  } = useViewer();

  const { t } = useTranslation();
  const { toast } = useToast();
  const isParent = track.parentTrackId === null;
  const [showDragHandle, setShowDragHandle] = useState(false);
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({
      id: track.id,
      transition: null,
      disabled: disabled || isArchived,
    });
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const isDeleteDisabled = isTrackDeleteDisabled(track.id, allTracks, role);

  const tracksById = useMemo(
    () => new Map(allTracks.map((t) => [t.id, t])),
    [allTracks],
  );
  const effectiveColor = getEffectiveTrackColor(track, tracksById);

  const { subTrackReorganizationFlag } = useFlags();
  const canViewArchivedTrack =
    isArchived &&
    !!openViewArchivedTrackDrawer &&
    (isParent || subTrackReorganizationFlag);

  // `parentTrack` is optional, so fall back to the shared track list. This only
  // decides whether Unarchive is offered: an archived sub-track whose parent is
  // also archived cannot be unarchived on its own, so the action is withheld.
  // An active sub-track never has an archived parent — archiving a parent
  // cascades to its children — so this only ever applies to an archived one.
  const parentTrackRow =
    parentTrack ??
    (track.parentTrackId ? tracksById.get(track.parentTrackId) : undefined);
  const isParentArchived = parentTrackRow?.archivedAt != null;

  // Zero when active, else tRPC; refetchTracks is a no-op under Zero.
  const { archiveTrack, unarchiveTrack, deleteTrack } = useTrackMutations();

  const handleDelete = () => {
    deleteTrack({ trackId: track.id, teamId: track.teamId })
      .then(() => {
        refetchTracks();
        toast({
          description: t('trackDeleted', { track: track.name }),
          severity: 'success',
          title: t('success'),
        });
      })
      .catch(() => {
        toast({ title: t('errorDeletingTrack'), severity: 'error' });
      });
  };

  const handleArchive = () => {
    archiveTrack({ trackId: track.id, teamId: track.teamId })
      .then(() => {
        refetchTracks();
        // Only a parent owns a collapse toggle; a sub-track row has none.
        if (isParent && !isCollapsed) {
          toggleCollapse(track.id);
        }
        toast({
          description: isParent ? t('trackArchived') : t('subTrackArchived'),
          severity: 'success',
          title: t('success'),
        });
      })
      .catch(() => {
        toast({
          title: isParent
            ? t('errorArchivingTrack')
            : t('errorArchivingSubTrack'),
          severity: 'error',
        });
      });
  };

  const handleUnarchive = () => {
    unarchiveTrack({ trackId: track.id, teamId: track.teamId })
      .then(() => {
        refetchTracks();
        toast({
          description: isParent
            ? t('trackUnarchived')
            : t('subTrackUnarchived'),
          severity: 'success',
          title: t('success'),
        });
      })
      .catch(() => {
        toast({
          title: isParent
            ? t('errorUnarchivingTrack')
            : t('errorUnarchivingSubTrack'),
          severity: 'error',
        });
      });
  };

  // Manual multi-team transfer option
  const multiTeamTransferOptions = track.multiTeamTransferOptions;
  const hasMultiTeamTransferOption =
    !enableTeamLibrary && !!multiTeamTransferOptions;

  const manualTransferTrackMutation =
    trpc.transfers.manualTransferTrack.useMutation();

  // Athlete transfer option - enabled when the athlete is transferred to a new team
  const athleteTransfer = track.athleteTransfer;
  const athleteTransferTeam = teams.find(
    (t) => t.id === athleteTransfer?.newTeamId,
  );
  const hasAthleteTransfer = !enableTeamLibrary && !!athleteTransfer;
  const hasTrackTransfer = hasMultiTeamTransferOption || hasAthleteTransfer;
  const transferTrackMutation = trpc.transfers.transferTrack.useMutation();
  const transferTeams =
    athleteTransfer && athleteTransferTeam
      ? [athleteTransferTeam]
      : multiTeamTransferOptions;

  const trackItem = (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        touchAction: 'none',
        ...stylesForIndex(index),
        // With the DragOverlay below following the pointer, this row becomes
        // dnd-kit's "placeholder" for the slot the drag is sourced from —
        // dimming it is the standard sortable pattern for telling the two
        // apart while dragging.
        ...(isDragging && { opacity: 0.4 }),
      }}
      onMouseOver={() => {
        setShowDragHandle(true);
      }}
      onMouseOut={() => {
        setShowDragHandle(false);
      }}
      className={cn(
        'flex flex-row w-full py-3 pr-3 items-center',
        isArchived
          ? 'pl-4 border-l-8 border-l-gray-400 bg-gray-50'
          : 'pl-6 bg-white',
        (isLast || active) && 'border-b',
        active && 'border-t',
        isParent && index !== 1 && !isFirstInSection && 'border-t',
        disabled && 'text-gray-300',
        dropHighlight === 'eligible' && 'bg-blue-50',
        dropHighlight === 'ineligible' && 'bg-red-50 cursor-not-allowed',
      )}
    >
      {/* Wraps the icon/color-dot/name so the whole area is one click/keyboard
          target for opening the view-archived-track drawer, kept as a sibling
          of (not a wrapper around) the collapse IconButton below — nesting
          the two interactive elements would be an a11y anti-pattern. */}
      <div
        onClick={
          canViewArchivedTrack
            ? () => openViewArchivedTrackDrawer(track.id)
            : undefined
        }
        onKeyDown={
          canViewArchivedTrack
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openViewArchivedTrackDrawer(track.id);
                }
              }
            : undefined
        }
        role={canViewArchivedTrack ? 'button' : undefined}
        tabIndex={canViewArchivedTrack ? 0 : undefined}
        aria-label={
          canViewArchivedTrack
            ? t('viewArchivedTrackAriaLabel', { trackName: track.name })
            : undefined
        }
        className={cn(
          'flex flex-row items-center flex-grow min-w-0',
          canViewArchivedTrack && 'cursor-pointer',
        )}
      >
        <TrackIcon
          isParent={isParent}
          attributes={attributes}
          listeners={listeners}
          showDragHandle={showDragHandle && !isArchived}
          disabled={disabled}
          track={track}
          hasTransfer={hasAthleteTransfer}
        />
        {effectiveColor && (
          <span
            aria-hidden="true"
            className="flex-shrink-0 mr-2 rounded-full"
            style={{ backgroundColor: effectiveColor, width: 8, height: 8 }}
          />
        )}
        <p
          // Only a track with sub-tracks has anything to collapse. An empty
          // parent renders no chevron, so toggling here would silently store
          // a collapsed state with no visible control to undo it.
          onClick={() =>
            isParent && hasSubtracks && !isArchived && toggleCollapse(track.id)
          }
          className={cn(
            'text-left flex-grow w-full font-semibold text-xs truncate',
            isParent && 'text-sm',
            isParent && hasSubtracks && !isArchived && 'cursor-pointer',
          )}
        >
          {track.name}
        </p>
      </div>
      {/* Only show collapse button on the parent track if there are sub-tracks */}
      {isParent && hasSubtracks && (
        <IconButton
          size="small"
          aria-label={
            isCollapsed
              ? t('expandTrack', { trackName: track.name })
              : t('collapseTrack', { trackName: track.name })
          }
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapse(track.id);
          }}
        >
          {isCollapsed ? (
            <MdKeyboardArrowDown aria-hidden="true" size={18} />
          ) : (
            <MdKeyboardArrowUp aria-hidden="true" size={18} />
          )}
        </IconButton>
      )}
      {transferModalOpen && transferTeams && (
        <MoveTrackModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          trackName={track.name}
          transferType={
            hasAthleteTransfer ? 'athleteTransfer' : 'multiTeamTransfer'
          }
          otherTeams={transferTeams}
          onConfirm={(newTeamId: string, doNothing: boolean) => {
            setTransferModalOpen(false);
            // Athlete transfer
            if (athleteTransfer) {
              // transfer and return early
              return transferTrackMutation.mutate(
                {
                  trackId: track.id,
                  teamId: athleteTransfer.newTeamId,
                  doNothing,
                },
                {
                  onSuccess: () => {
                    refetchTracks();
                    toast({
                      description: doNothing
                        ? t('trackTransferIndicatorCleared')
                        : t('trackHasBeenMoved', {
                            track: track.name,
                          }),
                      severity: 'success',
                      title: t('success'),
                    });
                  },
                },
              );
            }

            // Manual multi-team transfer
            const profileId = track.athletes[0]?.profileId;
            if (multiTeamTransferOptions && profileId && newTeamId) {
              manualTransferTrackMutation.mutate(
                {
                  trackId: track.id,
                  profileId,
                  oldTeamId: track.teamId,
                  newTeamId,
                },
                {
                  onSuccess: () => {
                    refetchTracks();
                    toast({
                      description: t('trackHasBeenMoved', {
                        track: track.name,
                      }),
                      severity: 'success',
                      title: t('success'),
                    });
                  },
                },
              );
            }
          }}
        />
      )}
      <DeleteConfirmationModal
        id={track.id}
        open={deleteModalOpen}
        {...deleteTrackText(t, !isParent)}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => {
          handleDelete();
          setDeleteModalOpen(false);
        }}
      />
    </div>
  );

  return (
    <TrackContextMenu
      trigger={trackItem}
      track={track}
      isParent={isParent}
      openAddTrackDrawer={openAddTrackDrawer}
      setTrackForEdit={setTrackForEdit}
      parentTrack={parentTrack}
      setTransferModalOpen={hasTrackTransfer ? setTransferModalOpen : undefined}
      isDeleteDisabled={isDeleteDisabled}
      onOpenDeleteModal={() => setDeleteModalOpen(true)}
      isArchived={isArchived}
      isParentArchived={isParentArchived}
      subTrackReorganizationFlag={subTrackReorganizationFlag}
      onArchive={handleArchive}
      onUnarchive={handleUnarchive}
      openMoveSubTrack={openMoveSubTrack}
    />
  );
}

const TrackIcon = ({
  isParent,
  showDragHandle,
  attributes,
  listeners,
  disabled,
  hasTransfer,
}: {
  isParent: boolean;
  showDragHandle: boolean;
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
  disabled: boolean;
  track: TrackPreview;
  hasTransfer: boolean;
}) => {
  const { t } = useTranslation();

  const IconPath = getIconPath(
    isParent,
    showDragHandle && !disabled && !hasTransfer,
  );

  return (
    <>
      <Tooltip
        title={hasTransfer ? t('athleteOnTrackHasBeenTransferred') : ''}
        placement="top"
        arrow
        disableInteractive
        disableHoverListener={!hasTransfer}
      >
        <div className="flex" {...attributes} {...listeners}>
          <IconPath
            aria-hidden="true"
            size={18}
            className={cn(
              'mr-2 text-gray-500',
              showDragHandle && !disabled && !hasTransfer && 'cursor-move',
              !isParent && 'ml-6',
              disabled
                ? 'text-gray-300'
                : hasTransfer
                  ? 'text-red-600 cursor-pointer'
                  : '',
            )}
          />
        </div>
      </Tooltip>
    </>
  );
};

function getIconPath(isParent: boolean, showDragHandle: boolean) {
  if (showDragHandle) return MdDragIndicator;
  return isParent ? MdPeople : MdSubdirectoryArrowRight;
}

export const TrackContextMenu = ({
  trigger,
  track,
  isParent,
  setTrackForEdit,
  parentTrack,
  openAddTrackDrawer,
  setTransferModalOpen,
  isDeleteDisabled,
  onOpenDeleteModal,
  isArchived,
  isParentArchived,
  subTrackReorganizationFlag,
  onArchive,
  onUnarchive,
  openMoveSubTrack,
}: {
  trigger: React.ReactNode;
  track: TrackPreview;
  isParent: boolean;
  parentTrack?: TrackPreview;
  setTrackForEdit: (forEdit: TrackForEdit) => void;
  openAddTrackDrawer: (open: boolean, parentTrack?: TrackPreview) => void;
  setTransferModalOpen?: (open: boolean) => void;
  isDeleteDisabled: boolean;
  onOpenDeleteModal: () => void;
  isArchived: boolean;
  isParentArchived: boolean;
  subTrackReorganizationFlag: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
  openMoveSubTrack?: (
    subTrack: TrackPreview,
    destinationTrack?: TrackPreview,
  ) => void;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const tracksCreate = hasPermissions('plan.tracks', 'create');
  const tracksEdit = hasPermissions('plan.tracks', 'edit');
  const tracksArchive = hasPermissions('plan.tracks', 'archive');
  const tracksDelete = hasPermissions('plan.tracks', 'delete');

  const subTrackMenuItems = getSubTrackContextMenuItems({
    isSubTrack: !isParent,
    isArchived,
    isParentArchived,
    flagEnabled: subTrackReorganizationFlag,
    moveAvailable: !!openMoveSubTrack,
  });

  // An archived track offers only the unarchive action — all other actions
  // (edit, add sub-track, move, delete) are hidden while archived. An archived
  // sub-track gets no menu at all while its parent is archived; unarchiving the
  // parent is the route back to active.
  if (isArchived) {
    if (!isParent && !subTrackMenuItems.includes('unarchive')) {
      return <>{trigger}</>;
    }

    return (
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild>{trigger}</ContextMenuTrigger>
        <ContextMenuContent>
          <DisabledWithTooltip
            disabled={!tracksArchive}
            reason={t('permissionDisabled.archive', { item: t('tracks') })}
          >
            <ContextMenuItem onClick={onUnarchive}>
              <MdUnarchive aria-hidden="true" size={18} className="mr-2" />
              {isParent ? t('unarchiveTrack') : t('unarchiveSubTrack')}
            </ContextMenuItem>
          </DisabledWithTooltip>
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  return (
    <>
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild>{trigger}</ContextMenuTrigger>
        <ContextMenuContent>
          <DisabledWithTooltip
            disabled={!tracksEdit}
            reason={t('permissionDisabled.edit', { item: t('tracks') })}
          >
            <ContextMenuItem
              onClick={() => setTrackForEdit({ id: track.id, parentTrack })}
            >
              {t('editTrack')}
            </ContextMenuItem>
          </DisabledWithTooltip>
          {isParent && (
            <DisabledWithTooltip
              disabled={!tracksCreate}
              reason={t('permissionDisabled.create', { item: t('tracks') })}
            >
              <ContextMenuItem
                onClick={() => {
                  openAddTrackDrawer(true, track);
                }}
              >
                {t('addSubTrack')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}
          {isParent && setTransferModalOpen && (
            <DisabledWithTooltip
              disabled={!tracksEdit}
              reason={t('permissionDisabled.edit', { item: t('tracks') })}
            >
              <ContextMenuItem onClick={() => setTransferModalOpen(true)}>
                {t('moveTrack')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}
          {/* The pointer-free route to the move a cross-parent drag performs.
              Only an active sub-track has one: the archived branch above
              returns early, so an archived sub-track's menu stays Unarchive. */}
          {subTrackMenuItems.includes('move') && (
            <DisabledWithTooltip
              disabled={!tracksEdit}
              reason={t('permissionDisabled.edit', { item: t('tracks') })}
            >
              <ContextMenuItem onClick={() => openMoveSubTrack?.(track)}>
                {t('moveToTrack')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}
          <DisabledWithTooltip
            disabled={!tracksDelete}
            reason={t('permissionDisabled.delete', { item: t('tracks') })}
          >
            <ContextMenuItem
              disabled={isDeleteDisabled}
              onClick={() => {
                if (!isDeleteDisabled) onOpenDeleteModal();
              }}
            >
              {t('deleteTrack')}
            </ContextMenuItem>
          </DisabledWithTooltip>
          {(isParent || subTrackMenuItems.includes('archive')) && (
            <DisabledWithTooltip
              disabled={!tracksArchive}
              reason={t('permissionDisabled.archive', { item: t('tracks') })}
            >
              <ContextMenuItem onClick={onArchive}>
                {isParent ? t('archiveTrack') : t('archiveSubTrack')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}
        </ContextMenuContent>
      </ContextMenu>
    </>
  );
};
